import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import AutoString from 'components/field/AutoString';
import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Icon from 'components/global/Icon';
import ProductImage from 'components/global/ProductImage';
import Radio from 'components/global/Radio';
import SelectButton from 'components/global/SelectButton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import ProductForm from 'pages/Products/Products/ProductForm';
import BoxRow from 'pages/Shipments/ActionForms/CreateShipment/Components/BoxRow';
import { getConnectedPartnersList } from 'pages/ShippingPartners/ConnectedPartners/ConnectedPartnersSlice.list';
import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE, ORDERS, ORDER_ACTIONS, PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { roundNumber } from 'utils/utils';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import * as S from '../QuickShipment.styled';

const colSpans = {
	large: { md: 12 },
	default: { md: 6 },
	small: { md: 3 }
};

const PICKUP_OPTIONS = [
	{
		id: 'otoRates',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/dropoffNearestBranch.png',
		title: 'OTORates',
		subtitle: 'otoRatesText',
		badge: <Badge variant="pill" color="teel" text="fastest" />
	},
	{
		id: 'myRates',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/pickupByCourier.png',
		title: 'createShipmentWithMyRates',
		subtitle: 'withMyRatesText'
	}
];

const valueOptions = { shouldValidate: true, shouldDirty: true };

const warehouseAutocompleteLabels = ['Name'];
const warehouseAutocompleteColumns = [
	{ accessor: 'Name', width: 200 },
	{ accessor: 'Code', width: 150 }
];

const productAutocompleteLabels = ['name'];
const productAutocompleteColumns = [
	{ accessor: 'name', width: 200 },
	{ accessor: 'hsCode', width: 200 },
	{ accessor: 'SKU', width: 200 }
];

const OrderDetails = ({ formMode, setPickupType, pickupType }) => {
	const { Currency } = useLocale();
	const { showSuccess, showError } = useAlert();
	const axios = useAxiosPrivate();
	const isAuthorized = useAccessControl();
	const connectedPartnersList = useSelector((state) => state.connectedPartners);
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const hasProduct = useSelector((state) => state.instruction.productCheck);
	const shippingCompanyRef = useRef(null);

	const { defaultBox: defaultBoxState } = useSelector((state) => state.user);
	const defaultBox = useMemo(
		() =>
			defaultBoxState
				? {
						ID: defaultBoxState.id,
						width: defaultBoxState.width,
						height: defaultBoxState.height,
						length: defaultBoxState.length,
						name: defaultBoxState.boxName,
						weight: 0
					}
				: { width: 10, height: 10, length: 10, weight: 0 },
		[defaultBoxState]
	);

	const formMethods = useFormContext();
	const {
		setValue,
		watch,
		control,
		getValues,
		trigger,
		setFocus,
		formState: { isLoading: isFormLoading, isSubmitting, errors }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	const currency = useWatch({ control, name: 'currency' });
	const grandTotal = useWatch({ control, name: 'grandTotal' });
	const paymentMethod = useWatch({ control, name: 'paymentMethod' });
	const warehouseID = useWatch({ control, name: 'warehouseID' });
	const productsWatcher = useWatch({ control, name: 'products' });
	const boxWatcher = useWatch({ control, name: 'boxes' });
	const isInternationalOrder = useWatch({ control, name: 'isInternationalOrder' });

	const hasActivePartner = useWatch({
		control,
		name: 'hasActivePartner'
	});

	const {
		fields,
		append: append,
		remove: remove
	} = useFieldArray({
		control,
		name: 'products'
	});

	const {
		fields: boxFields,
		append: appendBox,
		remove: removeBox
	} = useFieldArray({
		control,
		name: 'boxes'
	});

	const controlledFields = fields.map((field, index) => ({
		...field,
		...productsWatcher[index]
	}));

	useUpdateEffect(() => {
		const hasActivePartner = connectedPartnersList?.some((partner) => partner.active);
		setValue('hasActivePartner', hasActivePartner);
	}, [connectedPartnersList]);

	useEffect(() => {
		if (pickupType === 'myRates' && shippingCompanyRef.current) {
			shippingCompanyRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [pickupType]);

	const { addMode, editMode } = useMemo(
		() => ({
			addMode: formMode === 'add',
			editMode: formMode === 'edit'
		}),
		[formMode]
	);

	const grandTotalOfAllProducts = useMemo(
		() => controlledFields.reduce((acc, curr) => acc + (curr.total ?? 0), 0),
		[controlledFields]
	);

	useEffect(() => {
		if (grandTotal && paymentMethod === 'cod' && addMode) {
			setValue('totalDue', grandTotal, valueOptions);
		}
	}, [grandTotal, paymentMethod, addMode]);

	useEffect(() => {
		if (isInternationalOrder && productsWatcher.length === 0) {
			handleAddProduct();
		}
	}, [isInternationalOrder]);

	const removeItem = async ({ index, original: { ID } }) => {
		if (ID) {
			try {
				const response = await axios.post(ORDER_ACTIONS.DELETE_ORDER_ITEM, {
					otoOrderID: Number(watch('orderID')),
					ID
				});
				if (response.status === 200) {
					showSuccess();
					remove(index);
				} else {
					showError();
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError();
			}
		} else {
			remove(index);
		}
	};

	const setMultipleValues = useCallback(
		(values) => {
			Object.entries(values).forEach(([key, value]) => {
				setValue(key, value, valueOptions);
			});
		},
		[setValue]
	);

	const columns = useMemo(
		() => [
			{
				id: 'products',
				header: 'productName',
				size: isMobile ? 110 : 200,
				renderCell: ({ row }) => (
					<S.ProductWrapper>
						<S.ProductImageWrapper>
							<ProductImage
								data={{
									image: watch(`products[${row.index}].image`)
								}}
							/>
						</S.ProductImageWrapper>
						<S.ProductAutoCompleteWrapper>
							<AutoComplete
								key={row.original?.id}
								autoCompleteName={AUTOCOMPLETE.PRODUCTS}
								value={watch(`products[${row.index}].productID`)}
								columns={productAutocompleteColumns}
								fixedContainerWidth={false}
								name={`products[${row.index}].name`}
								selectionLabelAccessor={productAutocompleteLabels}
								setValue={(value, isFirstRender) => {
									if (!isFirstRender) {
										setMultipleValues({
											[`products[${row.index}].productID`]: value?.ID ?? null,
											[`products[${row.index}].name`]: value?.name ?? null,
											[`products[${row.index}].hsCode`]: value?.hsCode ?? null,
											[`products[${row.index}].SKU`]: value?.SKU ?? null,
											[`products[${row.index}].price`]: value?.price ?? null,
											[`products[${row.index}].tax`]: value?.tax ?? null,
											[`products[${row.index}].total`]:
												((value?.price ?? 0) + (value?.tax ?? 0)) *
												getValues(`products[${row.index}].quantity`),
											[`products[${row.index}].currency`]: value?.currency ?? currency,
											[`products[${row.index}].image`]: value?.image ?? null
										});
									}
								}}
								firstItem={
									isAuthorized('/autocomplete/product/addANewProduct')
										? {
												action: () =>
													NiceModal.show(ProductForm, {
														setProduct: (value) => {
															setValue(
																`products[${row.index}].productID`,
																value,
																valueOptions
															);
														}
													}),
												label: <Typography color="var(--blue)">addNewProduct</Typography>
											}
										: undefined
								}
								textValue={watch(`products[${row.index}].name`)}
								setTextValue={(value) => {
									setMultipleValues({
										[`products[${row.index}].productID`]: null,
										[`products[${row.index}].name`]: value
									});
								}}
							/>
						</S.ProductAutoCompleteWrapper>
					</S.ProductWrapper>
				),
				showOnMobile: true,
				enableHiding: false
			},
			...(isInternationalOrder
				? [
						{
							header: () => (
								<S.CustomHeaderContainer>
									<Typography fontWeight="500">hsCode</Typography>
									<S.Required>*</S.Required>
									<Tooltip title={'hsCodeTooltip'} placement="bottom">
										<Icon icon={faCircleInfo} size="small" />
									</Tooltip>
								</S.CustomHeaderContainer>
							),
							accessorKey: 'hsCode',
							size: isMobile ? 110 : 200,
							renderCell: ({ row }) => (
								<AutoStringCell
									hsCode={watch(`products[${row.index}].hsCode`)}
									setHsCode={(value) => {
										setValue(`products[${row.index}].hsCode`, value, valueOptions);
									}}
									errorMessage={errors?.products?.[row.index]?.hsCode?.message}
								/>
							)
						}
					]
				: []),
			{
				header: 'sku',
				accessorKey: 'SKU',
				size: 90,
				renderCell: ({ row }) => <StringInput name={`products[${row.index}].SKU`} />
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 90,
				maxSize: 90,
				renderCell: ({ row }) => (
					<IntegerInput
						errorMessage={errors?.products?.[row.index]?.quantity?.message}
						value={watch(`products[${row.index}].quantity`)}
						setValue={(value) => {
							setValue(`products[${row.index}].quantity`, value, valueOptions);
							const val =
								value *
								((getValues(`products[${row.index}].price`) ?? 0) +
									(getValues(`products[${row.index}].tax`) ?? 0));
							setValue(`products[${row.index}].total`, val ? Number(val.toFixed(2)) : val, valueOptions);
						}}
					/>
				)
			},
			{
				header: 'price',
				accessorKey: 'price',
				size: 100,
				maxSize: 150,
				renderCell: ({ row }) => (
					<IntegerInput
						value={watch(`products[${row.index}].price`)}
						setValue={(value) => {
							setValue(`products[${row.index}].price`, value, valueOptions);
							setValue(
								`products[${row.index}].total`,
								getValues(`products[${row.index}].quantity`) *
									((value ?? 0) + (getValues(`products[${row.index}].tax`) ?? 0)),
								valueOptions
							);
						}}
						unit={watch(`products[${row.index}].currency`) ?? Currency}
						showArrows={false}
						allowFloat
					/>
				)
			},
			{
				header: 'tax',
				accessorKey: 'tax',
				size: 100,
				maxSize: 150,
				renderCell: ({ row }) => (
					<IntegerInput
						value={watch(`products[${row.index}].tax`)}
						setValue={(value) => {
							setValue(`products[${row.index}].tax`, value, valueOptions);
							setValue(
								`products[${row.index}].total`,
								getValues(`products[${row.index}].quantity`) *
									((getValues(`products[${row.index}].price`) ?? 0) + (value ?? 0)),
								valueOptions
							);
						}}
						unit={watch(`products[${row.index}].currency`) ?? Currency}
						showArrows={false}
						allowFloat
					/>
				)
			},
			{
				header: 'total',
				accessorKey: 'total',
				size: 100,
				maxSize: 150,
				renderCell: ({ row }) => (
					<IntegerInput
						name={`products[${row.index}].total`}
						unit={watch(`products[${row.index}].currency`) ?? Currency}
						showArrows={false}
						allowFloat
					/>
				)
			},
			{
				id: 'deleteRow',
				size: isMobile ? 10 : 20,
				maxSize: isMobile ? 10 : 20,
				showOnMobile: true,
				enableHiding: false
			}
		],
		[isMobile, errors, isInternationalOrder]
	);

	const handleAddBox = useCallback(() => {
		appendBox(defaultBox);
	}, [defaultBox]);

	const handleAddProduct = useCallback(() => {
		append({
			productID: null,
			name: null,
			hsCode: null,
			SKU: null,
			quantity: 1,
			price: null,
			tax: null,
			total: null,
			currency
		});
		trigger('orderComments');
	}, [currency]);

	useUpdateEffect(() => {
		const currentWeight = getValues('weight');
		const currentPackageCount = getValues('packageCount');

		if (boxWatcher?.length > 0) {
			const newWeight = roundNumber(boxWatcher.reduce((acc, curr) => acc + curr.weight, 0));
			const newPackageCount = boxWatcher.length;
			if (currentWeight !== newWeight) {
				setValue('weight', newWeight, valueOptions);
			}
			if (currentPackageCount !== newPackageCount) {
				setValue('packageCount', newPackageCount, valueOptions);
			}
		}
	}, [boxWatcher]);

	const handleAdjustOrderValue = useCallback(() => {
		setValue('grandTotal', grandTotalOfAllProducts, valueOptions);
		if (paymentMethod?.toLowerCase() === 'cod') {
			setValue('totalDue', grandTotalOfAllProducts);
		} else {
			setValue('totalDue', null);
		}
		showSuccess({
			header: 'orderValueAdjusted',
			message: 'orderValueAdjustedMessage'
		});

		setTimeout(() => {
			setFocus('grandTotal');
		}, 300);
	}, [grandTotalOfAllProducts]);

	const getAutoFillOrderID = useCallback(async () => {
		try {
			const response = await axios.post(ORDERS.GET_AUTO_FILL_ORDER_ID, {});
			if (response.status === 200) {
				setValue('otoID', response?.data.orderID, valueOptions);
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	});

	useEffect(() => {
		dispatch(getConnectedPartnersList());

		if (!editMode) {
			getAutoFillOrderID();
		}
	}, []);

	const onRemove = useCallback(async (rowID) => {
		try {
			const response = await axios.post(ORDER_ACTIONS.DELETE_ORDER_BOX, {
				otoOrderID: Number(watch('orderID')),
				ID: rowID
			});
			if (response.status === 200) {
				showSuccess();
			} else {
				showError();
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	return (
		<S.CreateShipmentContent>
			<S.Title variant="p2" fontWeight="500">
				orderDetails
			</S.Title>
			<S.CreateShipmentContentWrapper>
				<FormItem colSpan={colSpans.default} label="orderNumber" isLoading={isLoading} required>
					<StringInput disabled={editMode} name="otoID" />
				</FormItem>

				<FormItem colSpan={colSpans.default} label="paymentMethod" required isLoading={isLoading}>
					<S.PaymentMethodContainer>
						{['paid', 'cod'].map((method, index) => {
							return (
								<S.Radio key={index}>
									<Radio
										onClick={() => {
											setValue('paymentMethod', method, valueOptions);
										}}
										checked={watch('paymentMethod') === method}
										readOnly
									/>
									<Typography variant="p3" component="div" fontWeight={500}>
										{method}
									</Typography>
								</S.Radio>
							);
						})}
					</S.PaymentMethodContainer>
				</FormItem>
				<S.OrderContainer>
					<FormItem
						className="form-item-warehouse"
						colSpan={colSpans.default}
						label="warehouse"
						required
						isLoading={isLoading}>
						<AutoComplete
							errorMessage={errors?.warehouseID?.message}
							disabled={editMode || !isAuthorized('/autocomplete/warehouse')}
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							setValue={(value) => {
								if (!value) {
									setValue('warehouseID', null, valueOptions);
									setValue('originCountry', null, valueOptions);
									setValue(
										'originAddress',
										{ cityName: null, countryName: null, text: null },
										valueOptions
									);
									return;
								}
								if (addMode) {
									setValue('warehouseID', value.ID, valueOptions);
									setValue('originCountry', value.Country, valueOptions);
									setValue(
										'originAddress',
										{
											cityName: value.City,
											countryName: value.Country,
											text: value.Address
										},
										valueOptions
									);
								}
							}}
							value={warehouseID}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
							firstItem={
								isAuthorized('/autocomplete/warehouse/addANewWarehouse')
									? {
											action: () =>
												NiceModal.show(WarehouseFormShort, {
													setWarehouse: (value) => {
														setValue('warehouseID', value, valueOptions);
													}
												}),
											label: <Typography color="var(--blue)">addANewWarehouse</Typography>
										}
									: undefined
							}
						/>
					</FormItem>
					<FormItem
						className="form-item-grandTotal"
						colSpan={paymentMethod?.toLowerCase() !== 'cod' ? colSpans.default : colSpans.small}
						label="grandTotal"
						isLoading={isLoading}
						required>
						{hasProduct ? (
							<CurrencyInput
								name="grandTotal"
								value={currency}
								setValue={(value) => {
									setValue(`currency`, value, valueOptions);
								}}
								currencyErrorMessage={errors?.currency?.message}
							/>
						) : (
							<IntegerInput
								name="grandTotal"
								value={watch('grandTotal') ?? ''}
								unit={Currency}
								showArrows={false}
							/>
						)}
					</FormItem>
					{paymentMethod?.toLowerCase() === 'cod' && (
						<FormItem
							className="form-item-totalDue"
							colSpan={colSpans.small}
							label="totalDue"
							isLoading={isLoading}
							required>
							{hasProduct ? (
								<CurrencyInput
									name="totalDue"
									value={currency}
									setValue={(value) => {
										setValue(`currency`, value, valueOptions);
									}}
									currencyErrorMessage={errors?.currency?.message}
								/>
							) : (
								<IntegerInput
									name="totalDue"
									value={watch('totalDue') ?? ''}
									unit={Currency}
									showArrows={false}
								/>
							)}
						</FormItem>
					)}
				</S.OrderContainer>
				<FormItem
					colSpan={colSpans.large}
					label="itemDescription"
					isLoading={isLoading}
					required={!productsWatcher?.length}>
					<TextArea name="orderComments" placeholder="orderCommentsPlaceholder" />
				</FormItem>
				<S.Line />
				{(hasProduct || isInternationalOrder) && (
					<>
						<FormItem>
							<Table
								columns={columns}
								data={controlledFields}
								loading={isLoading}
								noRecordsFound={<></>}
								onRowDelete={removeItem}
								deletableLastRow
							/>
						</FormItem>
						<FormItem classNameChild="products-buttons-form-item">
							<S.ProductsButtonWrapper>
								<Button label="addNewRow" icon={faPlus} variant="outline" onClick={handleAddProduct} />
								<Button
									label="adjustOrderValue"
									variant="transparent"
									onClick={handleAdjustOrderValue}
									style={{
										textDecoration: 'underline'
									}}
									disabled={
										controlledFields?.length < 1 ||
										grandTotalOfAllProducts < 1 ||
										grandTotalOfAllProducts === grandTotal
									}
								/>
							</S.ProductsButtonWrapper>
						</FormItem>
						<S.Line />
					</>
				)}
				<S.BoxDetailsTitleWrapper>
					<S.Title variant="p2" fontWeight="500">
						boxDetails
					</S.Title>
					<S.BoxDetails>
						<S.BoxWrapper>
							<Typography color="var(--content-2)">totalPackageCount</Typography>
							<Typography fontWeight="500">{`${watch('packageCount')}`}</Typography>
						</S.BoxWrapper>
						<S.VerticalLine />
						<S.BoxWrapper>
							<Typography color="var(--content-2)">totalWeight</Typography>
							<Typography fontWeight="500">{`${watch('weight')} ${t('kg').toUpperCase()}`}</Typography>
						</S.BoxWrapper>
					</S.BoxDetails>
				</S.BoxDetailsTitleWrapper>
				<S.BoxRowWrapper>
					{boxFields?.map((field, index) => (
						<BoxRow
							key={`${field.id}${index}`}
							index={index}
							remove={removeBox}
							disabledDeleteButton={boxFields.length === 1}
							fieldName="boxes"
							onRemove={onRemove}
						/>
					))}
				</S.BoxRowWrapper>

				<FormItem>
					<Button label="addBox" icon={faPlus} variant="outline" onClick={handleAddBox} />
				</FormItem>
				<Alert className="m-3" showIcon variant="info" content={'boxDetail.alert.message'} />

				{hasActivePartner && (
					<>
						<S.Line />
						<S.Title variant="p2" fontWeight="500">
							shippingOptions
						</S.Title>
						<S.PickupOptionsButtonContainer>
							{PICKUP_OPTIONS.map((option) => (
								<SelectButton
									key={option.id}
									onClick={() => setPickupType(option.id)}
									checked={pickupType === option.id}
									description={option.subtitle}
									badge={option.badge}
									title={option.title}
									image={option.image}
									variant={'radio'}
								/>
							))}
						</S.PickupOptionsButtonContainer>
						{pickupType === 'myRates' && (
							<>
								<S.Title variant="p2" fontWeight="500">
									selectYourShippingCompany
								</S.Title>
								<S.PickupOptionsButtonContainer ref={shippingCompanyRef} className="shippingCompany">
									{connectedPartnersList
										.filter((option) => option.active)
										.map((option) => (
											<SelectButton
												key={option.settingsID}
												onClick={() => {
													setValue('dcCompanyCompanyId', option.settingsID);
												}}
												image={option.icon}
												title={option.subname}
												checked={watch('dcCompanyCompanyId') === option.settingsID}
												variant={'radio'}
											/>
										))}
								</S.PickupOptionsButtonContainer>
							</>
						)}
					</>
				)}
			</S.CreateShipmentContentWrapper>
		</S.CreateShipmentContent>
	);
};

const AutoStringCell = ({ hsCode, setHsCode, errorMessage }) => {
	const axios = useAxiosPrivate();
	const debouncedText = useDebounce(hsCode, 1000);
	const [hsCodes, setHsCodes] = useState([]);

	useEffect(() => {
		if (!debouncedText || debouncedText.length < 3) {
			setHsCodes([]);
			return;
		}
		axios
			.post(PRODUCTS.GET_HS_CODES, {
				keyword: debouncedText || ''
			})
			.then((response) => {
				if (response.status === 200) {
					setHsCodes(response.data);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			});
	}, [debouncedText]);

	return (
		<AutoString
			name="ID"
			columns={[
				{ accessor: 'ID', width: 100 },
				{ accessor: 'Name', width: 400 }
			]}
			autoFilter={false}
			data={hsCodes}
			value={hsCode}
			setValue={setHsCode}
			errorMessage={errorMessage}
			placeholder={'hsCodePlaceholder'}
			fixedContainerWidth={false}
		/>
	);
};

export default OrderDetails;
